import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Search,
  Upload,
  Trash2,
  Copy,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import firebaseStorageService from "@/services/firebaseStorageService";
import { showError, showInfo } from "@/store/errorStore";

const ACCEPTED_MIMES = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
const MAX_FILE_SIZE_MB = 5;

function sanitizeBaseName(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9._-]/g, "");
}

function extractFileNameFromURL(url) {
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (match && match[1]) {
      const fullPath = decodeURIComponent(match[1]);
      return fullPath.split("/").pop();
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

export default function UploadsManagementModal({ onSelect }) {
  const [rows, setRows] = useState([]); // [{id,name,url}]
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // upload dialog
  const [openUpload, setOpenUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [imageName, setImageName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // delete confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    fetchList();
  }, []);

  async function fetchList() {
    setLoading(true);
    try {
      // Listar arquivos de todas as pastas do Firebase Storage
      const [productsUrls, categoriesUrls, bannersUrls] = await Promise.all([
        firebaseStorageService.listFiles("products/").catch(() => []),
        firebaseStorageService.listFiles("uploads/").catch(() => []),
        firebaseStorageService.listFiles("categories/").catch(() => []),
        firebaseStorageService.listFiles("banners/").catch(() => []),
      ]);

      // Combinar todas as URLs em um único array
      const allUrls = [...productsUrls, ...categoriesUrls, ...bannersUrls];

      // Transformar em objetos com id, name e url
      const images = allUrls.map((url, index) => ({
        id: `firebase-${index}`,
        name: extractFileNameFromURL(url),
        url: url,
      }));

      setRows(images);
      console.log("✅ Imagens carregadas do Firebase:", images.length);
    } catch (error) {
      console.error("❌ Erro ao listar imagens do Firebase:", error);
      showError(
        error.message || "Erro ao carregar imagens",
        "Erro de Carregamento"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const id = String(r.id ?? "").toLowerCase();
      const nm = String(r.name ?? "").toLowerCase();
      return id.includes(q) || nm.includes(q);
    });
  }, [rows, search]);

  function onChangeFile(e) {
    setUploadSuccess(false);
    const f = e.target.files?.[0];
    if (!f) return setFile(null);

    // Validar tipo
    if (!ACCEPTED_MIMES.includes(f.type)) {
      showError(
        "Somente imagens JPG, JPEG, PNG e WEBP são permitidas.",
        "Formato Inválido"
      );
      e.target.value = null;
      return;
    }

    // Validar tamanho
    if (!firebaseStorageService.isValidSize(f, MAX_FILE_SIZE_MB)) {
      showError(
        `Arquivo muito grande. Máximo: ${MAX_FILE_SIZE_MB}MB`,
        "Tamanho Inválido"
      );
      e.target.value = null;
      return;
    }

    setFile(f);
    if (!imageName) {
      setImageName(sanitizeBaseName(f.name.replace(/\.[^.]+$/, "")));
    }
  }

  async function doUpload(e) {
    e.preventDefault();
    setUploadSuccess(false);

    if (!file) {
      showError("Selecione um arquivo.", "Upload");
      return;
    }

    try {
      // Validar arquivo
      firebaseStorageService.validateFile(file, MAX_FILE_SIZE_MB);

      setUploading(true);
      setUploadProgress(0);

      // Nome do arquivo
      const timestamp = Date.now();
      const ext = file.name.toLowerCase().split(".").pop();
      const fileName = imageName
        ? `${sanitizeBaseName(imageName)}_${timestamp}.${ext}`
        : `${timestamp}_${file.name}`;

      // Upload para pasta 'uploads/' no Firebase
      const path = `uploads/${fileName}`;

      // Upload com progresso
      const downloadURL = await firebaseStorageService.uploadFileWithProgress(
        file,
        path,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      console.log("✅ Upload concluído:", downloadURL);

      setUploadSuccess(true);
      showInfo(`Imagem "${fileName}" enviada com sucesso!`, "Upload Concluído");

      // Aguardar 1 segundo para mostrar o sucesso
      setTimeout(async () => {
        setOpenUpload(false);
        setFile(null);
        setImageName("");
        setUploadProgress(0);
        setUploadSuccess(false);
        await fetchList();
      }, 1500);
    } catch (error) {
      console.error("❌ Erro no upload:", error);
      showError(error.message || "Falha no upload", "Erro no Upload");
      setUploading(false);
      setUploadProgress(0);
    }
  }

  function askDelete(item) {
    setToDelete(item);
    setConfirmOpen(true);
  }

  async function confirmDelete() {
    if (!toDelete) return;
    try {
      await firebaseStorageService.deleteFile(toDelete.url);
      showInfo(
        `Imagem "${toDelete.name}" deletada com sucesso!`,
        "Imagem Removida"
      );
      await fetchList();
    } catch (error) {
      console.error("❌ Erro ao deletar:", error);
      showError(error.message || "Erro ao deletar imagem", "Erro");
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  }

  function copyToClipboard(text) {
    try {
      navigator.clipboard.writeText(text);
    } catch {}
  }

  return (
    <div className="space-y-6">
      {/* Header + ações */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Uploads de Imagens</h2>
          <p className="text-sm text-gray-500 mt-1">
            {loading
              ? "Carregando..."
              : `${rows.length} imagens no Firebase Storage`}
          </p>
        </div>

        <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center">
          {/* Busca */}
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por nome ou ID..."
              className="pl-9 w-72"
            />
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {/* Upload */}
          <Dialog open={openUpload} onOpenChange={setOpenUpload}>
            <DialogTrigger asChild>
              <Button
                className=""
                style={{ background: "#c9332C" }}
                onMouseEnter={(e) => (e.target.style.background = "#a02820")}
                onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
              >
                <Upload className="w-4 h-4 mr-2" /> Enviar Imagem
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Upload para Firebase Storage</DialogTitle>
                <DialogDescription>
                  JPG, JPEG, PNG ou WEBP (até {MAX_FILE_SIZE_MB}MB).
                </DialogDescription>
              </DialogHeader>

              <form className="space-y-4" onSubmit={doUpload}>
                <div>
                  <Label htmlFor="file">Arquivo</Label>
                  <Input
                    id="file"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={onChangeFile}
                    disabled={uploading}
                  />
                </div>

                <div>
                  <Label htmlFor="imageName">
                    Nome da imagem (opcional, sem extensão)
                  </Label>
                  <Input
                    id="imageName"
                    placeholder="ex.: acai-200ml"
                    value={imageName}
                    onChange={(e) => setImageName(e.target.value)}
                    disabled={uploading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    O nome será sanitizado automaticamente (minúsculas, hífens).
                  </p>
                </div>

                {file && (
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="w-20 h-20 object-cover rounded-md border"
                    />
                    <div className="text-sm text-gray-600 flex-1">
                      <div className="font-medium">{file.name}</div>
                      <div className="text-xs">
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                )}

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        Enviando para Firebase...
                      </span>
                      <span className="font-medium">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                {uploadSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">
                      Upload concluído com sucesso!
                    </span>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setOpenUpload(false);
                      setFile(null);
                      setImageName("");
                      setUploadProgress(0);
                      setUploadSuccess(false);
                    }}
                    disabled={uploading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={!file || uploading}
                    style={{ background: uploading ? "#9ca3af" : "#c9332C" }}
                  >
                    {uploading ? "Enviando..." : "Enviar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              Carregando imagens do Firebase...
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">
            {search
              ? "Nenhuma imagem encontrada"
              : "Nenhuma imagem no Firebase Storage"}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {search
              ? "Tente outro termo de busca"
              : "Faça o primeiro upload para começar"}
          </p>
        </div>
      )}

      {/* Grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filtered.map((item) => {
            const url = item.url;
            return (
              <Card
                key={item.id}
                className="overflow-hidden hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-0">
                  <img
                    src={url}
                    alt={item.name}
                    className="w-full h-40 object-cover bg-gray-50 cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => window.open(url, "_blank")}
                    onError={(e) => {
                      e.currentTarget.src =
                        "data:image/svg+xml;utf8," +
                        encodeURIComponent(
                          `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='160'>
                          <rect width='100%' height='100%' fill='#f3f4f6'/>
                          <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#9ca3af' font-family='Arial' font-size='14'>
                            Preview indisponível
                          </text>
                        </svg>`
                        );
                    }}
                  />
                  <div className="p-3 space-y-2">
                    <div
                      className="text-sm font-semibold truncate"
                      title={item.name}
                    >
                      {item.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate" title={url}>
                      Firebase Storage
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          copyToClipboard(url);
                          showInfo(
                            "URL copiada para área de transferência",
                            "Copiado"
                          );
                        }}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copiar URL
                      </Button>

                      <AlertDialog
                        open={confirmOpen && toDelete?.id === item.id}
                        onOpenChange={(o) => {
                          setConfirmOpen(o);
                          if (!o) setToDelete(null);
                        }}
                      >
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => askDelete(item)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir imagem?</AlertDialogTitle>
                            <AlertDialogDescription>
                              A imagem <strong>{toDelete?.name}</strong> será
                              removida permanentemente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              onClick={() => setToDelete(null)}
                            >
                              Cancelar
                            </AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700"
                              onClick={confirmDelete}
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {onSelect && (
                      <div className="pt-2">
                        <Button
                          size="sm"
                          style={{ background: "#c9332C" }}
                          onClick={() => {
                            onSelect?.(url, item.name);
                            showInfo("Imagem selecionada", "Sucesso");
                          }}
                        >
                          Selecionar Imagem
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
